import React, { useState, useMemo, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { getRepRatingTier } from '../utils/iconResolver';
import {
  saveTicketChanges,
  dispatchTicketAction,
  dispatchTicketActions,
  createFnWorkorder,
  fetchTicketConfirmations,
  addTicketConfirmation,
  fetchRepsByCompletedProject,
  fetchCoordinatorProjects,
} from '../services/api';
import OpportunityPanel from './OpportunityPanel';
import TicketChatPanel from './TicketChatPanel';
import BulkOpportunityInvites from './BulkOpportunityInvites';
import './CoordinatorFilterPanel.css';

function ticketQbUrl(recordId) {
  return `https://siteservicesnow.quickbase.com/nav/app/bjt99j3pb/table/bjwdgf796/action/dr?rid=${recordId}`;
}

const PAY_TYPE_CHOICES = ['Blended', 'Fixed Rate', 'Hourly', 'Employee', 'Warranty', 'Deliverables Only'];
const SHIP_TO_CHOICES = ['Ship to TECH', 'Ship to SITE', 'Ship to ALT 1 HOLD LOCATION'];
const CONFIRMATION_TYPES = [
  'Recruiting Calls', 'Checked In', 'Checked Out', 'Day Of', 'Parts Requested',
  'Amazon Installation Appt Confirm', 'Attempted', 'Day Out', 'Email Confirm',
  'End of Day', 'FN Checked IN', 'FN Checked OUT', 'FN Provider Message',
  'FN Provider Requested', 'FN Reported Problem', 'FN Work Completed',
  'Job Follow Up', 'Keys', 'Marked FN Incomplete', 'New ETA', 'Notes',
  'Parts Return', 'Problem', 'QA Ready', 'Reschedule', 'Scope', 'Site Contact',
  'Tech Support', 'Tracking', 'Week Out',
];

const EDITABLE_FIELD_LABELS = {
  scheduleDate: 'Schedule Date',
  relatedFieldRepId: 'Field Rep',
  payType: 'Pay Type',
  fixedRate: 'Fixed Rate',
  firstHourRate: 'First Hour Rate',
  hourlyRate: 'Hourly Rate',
  travel: 'Travel',
  minHours: 'Min Hours',
  expectedHours: 'Expected Hours',
  maxHours: 'Max Hours',
  shipToLocation: 'Ship To',
  siteVisitInstructions: 'Site Visit Instructions',
  description: 'Description',
};

const BULK_EDIT_FIELDS = [
  'description',
  'siteVisitInstructions',
  'payType',
  'fixedRate',
  'firstHourRate',
  'hourlyRate',
  'travel',
  'minHours',
  'expectedHours',
  'maxHours',
  'scheduleDate',
  'relatedFieldRepId',
  'shipToLocation',
];

/**
 * Loose equality matching the backend's valuesEqual — used for change detection.
 * Treats null/undefined/'' as equivalent and compares numeric strings as numbers.
 */
function valuesEqualish(a, b) {
  const isEmpty = v => v === null || v === undefined || v === '';
  if (isEmpty(a) && isEmpty(b)) return true;
  if (isEmpty(a) !== isEmpty(b)) return false;
  // Only do numeric comparison when BOTH values are fully numeric. Naive
  // parseFloat would treat "2026-05-22" as 2026 and falsely equal "2026-08-15".
  const isFullNumber = v => typeof v === 'number' || /^-?\d+(?:\.\d+)?$/.test(String(v).trim());
  if (isFullNumber(a) && isFullNumber(b)) {
    return parseFloat(a) === parseFloat(b);
  }
  return String(a) === String(b);
}

/**
 * Normalize a QB date value (often ISO with time) to YYYY-MM-DD for <input type="date">.
 */
function toDateInputValue(v) {
  if (!v) return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/**
 * Format a UTC date string to Pacific time (DST-aware)
 */
function formatDatePST(dateStr) {
  if (!dateStr) return '—';
  try {
    // Plain date strings (no time component, e.g. "2026-04-01")
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m, d] = dateStr.split('-');
      return `${parseInt(m)}/${parseInt(d)}/${y}`;
    }
    // ISO timestamps — convert to Pacific
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const formatted = date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    // Determine PST vs PDT
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    const stdOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    const pacificOffset = new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' });
    const tzAbbr = pacificOffset.match(/[A-Z]{3,4}$/)?.[0] || 'PT';
    return `${formatted} ${tzAbbr}`;
  } catch {
    return dateStr;
  }
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return `${Math.round(n * 100)}%`;
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * Render trusted HTML from a QuickBase pipeline (e.g. FID 361 OTR/RES/ABDN).
 * The pipeline output is controlled and not user-editable; if that ever changes,
 * swap in DOMPurify here.
 */
function ComplianceBar({ html }) {
  if (!html) return <span style={{ color: '#9ca3af' }}>—</span>;
  return <span className="compliance-bar" dangerouslySetInnerHTML={{ __html: html }} />;
}

function CheckInCard({ checkIn }) {
  if (!checkIn) return null;
  return (
    <div className="checkin-card">
      <div className="checkin-card-title">Check-in</div>
      {checkIn.repName && (
        <div className="checkin-card-row"><span>Tech</span><strong>{checkIn.repName}</strong></div>
      )}
      {checkIn.checkedInLabel && (
        <div className="checkin-card-row"><span>Checked in</span><strong>{checkIn.checkedInLabel}</strong></div>
      )}
      {checkIn.distanceLabel && (
        <div className="checkin-card-row"><span>Distance</span><strong>{checkIn.distanceLabel}</strong></div>
      )}
      {checkIn.liveUrl && (
        <div className="checkin-card-row">
          <span>Live view</span>
          <a href={checkIn.liveUrl} target="_blank" rel="noopener noreferrer">Open</a>
        </div>
      )}
    </div>
  );
}

/**
 * Render a rating as filled stars (in tier color) + outlined stars for the unreached tiers.
 * Returns a "No Rating" label for tier 0.
 */
function renderStars(rating) {
  const { tier, color } = getRepRatingTier(rating);
  if (tier === 0) return <span className="rating-stars no-rating">No Rating</span>;
  return (
    <span className="rating-stars" aria-label={`${tier} of 5 stars`}>
      <span className="rating-stars-filled" style={{ color }}>{'★'.repeat(tier)}</span>
      <span className="rating-stars-empty">{'☆'.repeat(5 - tier)}</span>
    </span>
  );
}

const RATING_TIERS = [
  { tier: 5, color: '#4CAF50', label: '5 stars' },
  { tier: 4, color: '#8BC34A', label: '4 stars' },
  { tier: 3, color: '#FFC107', label: '3 stars' },
  { tier: 2, color: '#FF9800', label: '2 stars' },
  { tier: 1, color: '#F44336', label: '1 star' },
  { tier: 0, color: '#9E9E9E', label: 'No Rating' },
];

// Split a QB multitext value (comma-joined string) into trimmed values.
const splitMulti = s => (s ? String(s).split(',').map(v => v.trim()).filter(Boolean) : []);

// Sentinel option for reps with no value in a multitext field, so "no
// classification" is explicitly selectable rather than only visible when the
// filter is off entirely.
const NO_VALUE = '(None)';

const PRESET_CHIPS = [
  { id: 'scheduledToday', label: 'Scheduled Today' },
  { id: 'pastDue', label: 'Past Due' },
  { id: 'checkedIn', label: 'Checked In' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'all', label: 'All' },
  { id: 'fieldReps', label: 'Field Reps' },
];

function dateKey(value) {
  if (!value) return '';
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

function pacificToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
}

export default function CoordinatorFilterPanel({
  tickets = [],
  fieldReps = [],
  layerVisibility,
  onLayerVisibilityChange,
  liveLocationEnabled = false,
  liveRepCount = 0,
  liveWindowHours = 8,
  onLiveWindowChange,
  labelPrefs = {
    ticketLabels: true,
    fieldRepLabels: true,
    liveRepLabels: true,
    ticketLabelField: 'siteName',
  },
  onLabelPrefsChange,
  filters,
  onFiltersChange,
  ticketsLastFetched,
  onRefreshTickets,
  refreshingTickets = false,
  repLastSynced,
  onRefreshReps,
  refreshingReps = false,
  messagesLastFetched,
  onRefreshMessages,
  refreshingMessages = false,
  activePreset = null,
  onApplyPreset,
  hiddenCount = 0,
  onRestoreHidden,
}) {
  const [expandedSections, setExpandedSections] = useState({
    layers: true,
    ticketFilters: false,
    repFilters: false,
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [qbProjects, setQbProjects] = useState(null); // null = use ticket-derived list
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Compute unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    const projects = new Set();
    const serviceTypes = new Set();
    const states = new Set();
    const fscValues = new Set();
    const fieldRepNames = new Set();
    const statuses = new Set();
    // id → name for completed-history project picker (from active ticket cache)
    const projectById = new Map();

    tickets.forEach(t => {
      if (t.projectName) projects.add(t.projectName);
      if (t.serviceType) serviceTypes.add(t.serviceType);
      if (t.state) states.add(t.state);
      fscValues.add(t.fsc || '');
      fieldRepNames.add(t.fieldRepName || '');
      if (t.status) statuses.add(t.status);
      const pid = parseInt(t.relatedProjectId, 10);
      if (Number.isFinite(pid) && pid > 0 && t.projectName) {
        projectById.set(pid, t.projectName);
      }
    });

    const historyProjects = [...projectById.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      projects: [...projects].sort(),
      historyProjects,
      serviceTypes: [...serviceTypes].sort(),
      fscValues: [...fscValues].sort((a, b) => {
        if (a === '') return -1;
        if (b === '') return 1;
        return a.localeCompare(b);
      }),
      fieldRepNames: [...fieldRepNames].sort((a, b) => {
        if (a === '') return -1;
        if (b === '') return 1;
        return a.localeCompare(b);
      }),
      statuses: [...statuses].sort(),
      states: [...states].sort(),
    };
  }, [tickets]);

  // Compute counts
  const counts = useMemo(() => {
    const unassigned = tickets.filter(t => !t.fieldRepName).length;
    const assigned = tickets.filter(t => t.fieldRepName).length;

    const repsByTier = {};
    RATING_TIERS.forEach(t => { repsByTier[t.tier] = 0; });
    fieldReps.forEach(r => {
      const { tier } = getRepRatingTier(r.rating);
      repsByTier[tier] = (repsByTier[tier] || 0) + 1;
    });

    const paymentMethods = new Set();
    const repCategories = new Set();
    const classifications = new Set();
    let anyBlankClassification = false;
    fieldReps.forEach(r => {
      if (r.paymentMethod) paymentMethods.add(r.paymentMethod);
      splitMulti(r.repCategory).forEach(v => repCategories.add(v));
      const cls = splitMulti(r.classification);
      if (cls.length === 0) anyBlankClassification = true;
      cls.forEach(v => classifications.add(v));
    });

    return {
      unassigned, assigned, reps: fieldReps.length, repsByTier,
      paymentMethods: [...paymentMethods].sort(),
      repCategories: [...repCategories].sort(),
      classifications: [
        ...(anyBlankClassification ? [NO_VALUE] : []),
        ...[...classifications].sort(),
      ],
    };
  }, [tickets, fieldReps]);

  // Live count of reps meeting the min-jobs threshold (kept out of `counts` so
  // dragging the slider doesn't recompute tier/payment buckets).
  const repsMeetingJobs = useMemo(() => {
    const min = filters.repMinJobs12mo || 0;
    if (min <= 0) return fieldReps.length;
    return fieldReps.filter(r => (parseInt(r.last12Months, 10) || 0) >= min).length;
  }, [fieldReps, filters.repMinJobs12mo]);

  const historyCriteriaReady = Boolean(filters.repHistoryProjectId);

  const updateHistoryCriteria = (patch) => {
    setHistoryError(null);
    onFiltersChange(prev => ({
      ...prev,
      ...patch,
      // Changing criteria invalidates a previous Apply until they re-run it
      repHistoryRepIds: null,
      repHistoryTotalVisits: null,
    }));
  };

  const applyHistoryFilter = async () => {
    if (!historyCriteriaReady || historyLoading) return;
    const from = filters.repHistoryDateFrom || null;
    const to = filters.repHistoryDateTo || null;
    if (from && to && from > to) {
      setHistoryError('Start date must be on or before end date');
      return;
    }

    const projectId = filters.repHistoryProjectId;

    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await fetchRepsByCompletedProject({ projectId, from, to });
      onFiltersChange(prev => ({
        ...prev,
        // Only apply if criteria still match what we queried
        ...(prev.repHistoryProjectId === projectId
          && (prev.repHistoryDateFrom || null) === from
          && (prev.repHistoryDateTo || null) === to
          ? {
              repHistoryRepIds: new Set(data.repIds || []),
              repHistoryTotalVisits: data.totalVisits ?? 0,
            }
          : {}),
      }));
    } catch (err) {
      setHistoryError(err.message || 'Failed to load completed history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const clearHistoryFilter = () => {
    setHistoryError(null);
    onFiltersChange(prev => ({
      ...prev,
      repHistoryProjectId: null,
      repHistoryDateFrom: null,
      repHistoryDateTo: null,
      repHistoryRepIds: null,
      repHistoryTotalVisits: null,
    }));
  };

  const historyOnMapCount = useMemo(() => {
    if (!filters.repHistoryRepIds) return null;
    let n = 0;
    for (const r of fieldReps) {
      if (filters.repHistoryRepIds.has(Number(r.repId))) n += 1;
    }
    return n;
  }, [filters.repHistoryRepIds, fieldReps]);

  const historyProjectOptions = qbProjects || uniqueValues.historyProjects;

  const refreshProjects = async () => {
    if (projectsLoading) return;
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const data = await fetchCoordinatorProjects();
      setQbProjects(data.projects || []);
    } catch (err) {
      setProjectsError(err.message || 'Failed to load projects');
    } finally {
      setProjectsLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // Format relative time
  const formatLastSynced = (isoStr) => {
    if (!isoStr) return 'Never';
    const diff = now - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ${mins % 60}m ago`;
  };

  const presetCounts = useMemo(() => {
    const today = pacificToday();
    let scheduledToday = 0;
    let pastDue = 0;
    let unassigned = 0;
    let checkedIn = 0;
    for (const t of tickets) {
      if (!t.fieldRepName) unassigned += 1;
      if (t.checkIn) checkedIn += 1;
      if (dateKey(t.scheduleDate) === today) scheduledToday += 1;
      const due = dateKey(t.dueDate);
      if (due && due < today) pastDue += 1;
    }
    return {
      scheduledToday,
      pastDue,
      checkedIn,
      unassigned,
      all: tickets.length,
      fieldReps: fieldReps.length,
    };
  }, [tickets, fieldReps]);

  return (
    <div className="coord-filter-panel">
      <div className="preset-bar" role="toolbar" aria-label="Preset filters">
        {PRESET_CHIPS.map(chip => (
          <button
            key={chip.id}
            type="button"
            className={`preset-chip${activePreset === chip.id ? ' active' : ''}`}
            onClick={() => onApplyPreset && onApplyPreset(chip.id)}
          >
            {chip.label}
            <span className="preset-count">{presetCounts[chip.id]}</span>
          </button>
        ))}
        {hiddenCount > 0 && (
          <button type="button" className="preset-chip preset-restore" onClick={onRestoreHidden}>
            Show hidden ({hiddenCount})
          </button>
        )}
        <button
          type="button"
          className="preset-chip preset-add"
          title="Suggest a preset"
          aria-label="Suggest a preset"
          onClick={() => onApplyPreset && onApplyPreset('suggest')}
        >
          +
        </button>
      </div>
      {/* Search bar — always visible. Matches across ticket recordId, siteName,
          description, city, FN WO#, fieldRepName, and rep name / rep ID.
          Whitespace-tokenized AND match. */}
      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search map"
          title="Searches: Record ID, Site, Description, City, FN WO#, Field Rep name, Checked-in tech, Rep ID"
          value={filters.search || ''}
          onChange={e => onFiltersChange({ ...filters, search: e.target.value })}
        />
        {filters.search && (
          <button
            className="search-clear"
            aria-label="Clear search"
            onClick={() => onFiltersChange({ ...filters, search: '' })}
          >
            ×
          </button>
        )}
      </div>
      <div className="search-hint">
        Searches Record ID · Site · Description · City · FN WO# · Rep name / ID · Checked-in tech
      </div>

      {/* Layer Toggles */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('layers')}>
          <span>{expandedSections.layers ? '▾' : '▸'} Layers</span>
        </div>
        {expandedSections.layers && (
          <div className="section-content">
            <label className="layer-toggle">
              <input
                type="checkbox"
                checked={layerVisibility.unassigned}
                onChange={e => onLayerVisibilityChange({ ...layerVisibility, unassigned: e.target.checked })}
              />
              <span className="layer-dot" style={{ background: '#f59e0b' }} />
              Unassigned ({counts.unassigned})
            </label>
            <label className="layer-toggle">
              <input
                type="checkbox"
                checked={layerVisibility.assigned}
                onChange={e => onLayerVisibilityChange({ ...layerVisibility, assigned: e.target.checked })}
              />
              <span className="layer-dot" style={{ background: '#3b82f6' }} />
              Assigned ({counts.assigned})
            </label>
            <label className="layer-toggle">
              <input
                type="checkbox"
                checked={layerVisibility.fieldReps}
                onChange={e => onLayerVisibilityChange({ ...layerVisibility, fieldReps: e.target.checked })}
              />
              <span className="layer-dot" style={{ background: '#4CAF50' }} />
              Field Reps ({counts.reps})
            </label>
            {liveLocationEnabled && (
              <>
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={layerVisibility.liveReps}
                    onChange={e => onLayerVisibilityChange({ ...layerVisibility, liveReps: e.target.checked })}
                  />
                  <span className="layer-dot" style={{ background: '#4CAF50', boxShadow: '0 0 0 2px #fff, 0 0 0 3px #4CAF50' }} />
                  Live Locations ({liveRepCount})
                </label>
                {layerVisibility.liveReps && (
                  <div className="live-window-slider">
                    <label>Active within: {liveWindowHours}h</label>
                    <input
                      type="range"
                      min="0"
                      max="24"
                      step="1"
                      value={liveWindowHours}
                      onChange={e => onLiveWindowChange && onLiveWindowChange(Number(e.target.value))}
                    />
                  </div>
                )}
              </>
            )}

            <div className="labels-subsection">
              <div className="labels-subsection-title">Labels</div>
              <div className="label-toggle-row">
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={labelPrefs.ticketLabels}
                    onChange={e => onLabelPrefsChange && onLabelPrefsChange({ ticketLabels: e.target.checked })}
                  />
                  Ticket labels
                </label>
                <select
                  className="label-field-select"
                  value={labelPrefs.ticketLabelField || 'siteName'}
                  disabled={!labelPrefs.ticketLabels}
                  onChange={e => onLabelPrefsChange && onLabelPrefsChange({ ticketLabelField: e.target.value })}
                  title="Ticket label field"
                >
                  <option value="siteName">Site Name</option>
                  <option value="recordId">Record ID</option>
                  <option value="projectName">Project</option>
                  <option value="serviceType">Service Type</option>
                  <option value="itemName">Item Name</option>
                  <option value="status">Status</option>
                  <option value="fieldRepName">Field Rep</option>
                  <option value="city">City</option>
                  <option value="state">State</option>
                  <option value="dueDate">Due Date</option>
                  <option value="scheduleDate">Schedule Date</option>
                  <option value="startDate">Start Date</option>
                </select>
              </div>
              <label className="layer-toggle">
                <input
                  type="checkbox"
                  checked={labelPrefs.fieldRepLabels}
                  onChange={e => onLabelPrefsChange && onLabelPrefsChange({ fieldRepLabels: e.target.checked })}
                />
                Field Rep labels
              </label>
              {liveLocationEnabled && (
                <label className="layer-toggle">
                  <input
                    type="checkbox"
                    checked={labelPrefs.liveRepLabels}
                    onChange={e => onLabelPrefsChange && onLabelPrefsChange({ liveRepLabels: e.target.checked })}
                  />
                  Live Location labels
                </label>
              )}
            </div>

            <div className="rep-sync-info">
              <span className="sync-label">Ticket data: {formatLastSynced(ticketsLastFetched)}</span>
              <button
                className="refresh-btn"
                onClick={onRefreshTickets}
                disabled={refreshingTickets}
              >
                {refreshingTickets ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            <div className="rep-sync-info">
              <span className="sync-label">Rep data: {formatLastSynced(repLastSynced)}</span>
              <button
                className="refresh-btn"
                onClick={onRefreshReps}
                disabled={refreshingReps}
              >
                {refreshingReps ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            <div className="rep-sync-info">
              <span className="sync-label">Message data: {formatLastSynced(messagesLastFetched)}</span>
              <button
                className="refresh-btn"
                onClick={onRefreshMessages}
                disabled={refreshingMessages}
              >
                {refreshingMessages ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Filters */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('ticketFilters')}>
          <span>{expandedSections.ticketFilters ? '▾' : '▸'} Ticket Filters</span>
        </div>
        {expandedSections.ticketFilters && (
          <div className="section-content">
            <MultiSelectDropdown
              label="Project"
              selected={filters.projectNames}
              options={uniqueValues.projects}
              onChange={v => updateFilter('projectNames', v)}
            />
            <MultiSelectDropdown
              label="Service Type"
              selected={filters.serviceTypes}
              options={uniqueValues.serviceTypes}
              onChange={v => updateFilter('serviceTypes', v)}
            />
            <MultiSelectDropdown
              label="State"
              selected={filters.states}
              options={uniqueValues.states}
              onChange={v => updateFilter('states', v)}
            />
            <MultiSelectDropdown
              label="FSC"
              selected={filters.fscValues}
              options={uniqueValues.fscValues}
              onChange={v => updateFilter('fscValues', v)}
              renderOption={opt => opt === '' ? '(None)' : opt}
            />
            <MultiSelectDropdown
              label="Field Rep"
              selected={filters.fieldRepNames}
              options={uniqueValues.fieldRepNames}
              onChange={v => updateFilter('fieldRepNames', v)}
              renderOption={opt => opt === '' ? '(Unassigned)' : opt}
            />
            <MultiSelectDropdown
              label="Status"
              selected={filters.statuses}
              options={uniqueValues.statuses}
              onChange={v => updateFilter('statuses', v)}
            />
            <div className="filter-row">
              <label className="filter-label">Due Date</label>
              <div className="date-range">
                <input
                  type="date"
                  value={filters.dueDateFrom || ''}
                  onChange={e => updateFilter('dueDateFrom', e.target.value || null)}
                />
                <span>to</span>
                <input
                  type="date"
                  value={filters.dueDateTo || ''}
                  onChange={e => updateFilter('dueDateTo', e.target.value || null)}
                />
              </div>
            </div>
            <div className="filter-row">
              <label className="filter-label">Start Date</label>
              <div className="date-range">
                <input
                  type="date"
                  value={filters.startDateFrom || ''}
                  onChange={e => updateFilter('startDateFrom', e.target.value || null)}
                />
                <span>to</span>
                <input
                  type="date"
                  value={filters.startDateTo || ''}
                  onChange={e => updateFilter('startDateTo', e.target.value || null)}
                />
              </div>
            </div>
            <div className="filter-row">
              <label className="filter-label">Schedule Date</label>
              <div className="date-range">
                <input
                  type="date"
                  value={filters.scheduleDateFrom || ''}
                  onChange={e => updateFilter('scheduleDateFrom', e.target.value || null)}
                />
                <span>to</span>
                <input
                  type="date"
                  value={filters.scheduleDateTo || ''}
                  onChange={e => updateFilter('scheduleDateTo', e.target.value || null)}
                />
              </div>
            </div>
            <button
              className="clear-filters-btn"
              onClick={() => onFiltersChange({
                ...filters,
                search: '',
                preset: null,
                projectNames: null, serviceTypes: null, states: null,
                fscValues: null, fieldRepNames: null, statuses: null,
                dueDateFrom: null, dueDateTo: null, startDateFrom: null, startDateTo: null,
                scheduleDateFrom: null, scheduleDateTo: null,
              })}
            >
              Clear Ticket Filters
            </button>
          </div>
        )}
      </div>

      {/* Field Rep Filters */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('repFilters')}>
          <span>{expandedSections.repFilters ? '▾' : '▸'} Field Rep Filters</span>
        </div>
        {expandedSections.repFilters && (
          <div className="section-content">
            <div className="rating-tiers">
              {RATING_TIERS.map(({ tier, color, label }) => (
                <label key={tier} className="rating-toggle">
                  <input
                    type="checkbox"
                    checked={filters.repRatingTiers.has(tier)}
                    onChange={e => {
                      const next = new Set(filters.repRatingTiers);
                      if (e.target.checked) next.add(tier); else next.delete(tier);
                      updateFilter('repRatingTiers', next);
                    }}
                  />
                  <span className="rating-dot" style={{ background: color }} />
                  {label} ({counts.repsByTier[tier] || 0})
                </label>
              ))}
            </div>
            <MultiSelectDropdown
              label="Payment Method"
              selected={filters.repPaymentMethod}
              options={counts.paymentMethods}
              onChange={v => updateFilter('repPaymentMethod', v)}
            />
            <MultiSelectDropdown
              label="Rep Category"
              selected={filters.repCategory}
              options={counts.repCategories}
              onChange={v => updateFilter('repCategory', v)}
            />
            <MultiSelectDropdown
              label="Classification"
              selected={filters.classification}
              options={counts.classifications}
              onChange={v => updateFilter('classification', v)}
            />
            <div className="rep-jobs-filter">
              <label className="filter-label">
                Min jobs (12&nbsp;mo): {filters.repMinJobs12mo}{filters.repMinJobs12mo >= 100 ? '+' : ''}
                {filters.repMinJobs12mo > 0 && (
                  <span className="rep-jobs-count"> · {repsMeetingJobs} reps</span>
                )}
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={filters.repMinJobs12mo}
                onChange={e => updateFilter('repMinJobs12mo', Number(e.target.value))}
              />
            </div>

            <div className="rep-history-filter">
              <label className="filter-label">Completed on project</label>
              <p className="section-desc" style={{ fontSize: 11, color: '#6b7280', margin: '0 0 6px' }}>
                Show reps who completed Site Visits on a project. Dates are optional —
                leave both blank for all-time, or set one bound for an open range.
                {qbProjects
                  ? ` ${qbProjects.length} projects loaded from QuickBase.`
                  : ' Project list starts from active tickets — refresh for the full list.'}
              </p>
              <div className="filter-row rep-history-project-row">
                <select
                  value={filters.repHistoryProjectId || ''}
                  onChange={e => {
                    const v = e.target.value;
                    updateHistoryCriteria({
                      repHistoryProjectId: v ? Number(v) : null,
                    });
                  }}
                >
                  <option value="">Select project…</option>
                  {historyProjectOptions.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.status ? `${p.name} (${p.status})` : p.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="rep-history-refresh-btn"
                  title="Load full project list from QuickBase"
                  disabled={projectsLoading}
                  onClick={refreshProjects}
                >
                  {projectsLoading ? '…' : '↻'}
                </button>
              </div>
              {projectsError && (
                <div className="rep-history-error">{projectsError}</div>
              )}
              <div className="filter-row">
                <label className="filter-label">Completion date (optional)</label>
                <div className="date-range">
                  <input
                    type="date"
                    value={filters.repHistoryDateFrom || ''}
                    onChange={e => updateHistoryCriteria({
                      repHistoryDateFrom: e.target.value || null,
                    })}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={filters.repHistoryDateTo || ''}
                    onChange={e => updateHistoryCriteria({
                      repHistoryDateTo: e.target.value || null,
                    })}
                  />
                </div>
              </div>
              {historyError && (
                <div className="rep-history-error">{historyError}</div>
              )}
              {filters.repHistoryRepIds && (
                <div className="rep-history-status">
                  {filters.repHistoryRepIds.size} rep{filters.repHistoryRepIds.size === 1 ? '' : 's'} in history
                  {historyOnMapCount != null && historyOnMapCount !== filters.repHistoryRepIds.size && (
                    <> · {historyOnMapCount} on map</>
                  )}
                  {filters.repHistoryTotalVisits != null && (
                    <> · {filters.repHistoryTotalVisits} completed visit{filters.repHistoryTotalVisits === 1 ? '' : 's'}</>
                  )}
                </div>
              )}
              <div className="rep-history-actions">
                <button
                  type="button"
                  className="clear-filters-btn"
                  style={{ flex: 1, marginTop: 0 }}
                  disabled={!historyCriteriaReady || historyLoading}
                  onClick={applyHistoryFilter}
                >
                  {historyLoading ? 'Querying…' : (filters.repHistoryRepIds ? 'Re-apply' : 'Apply')}
                </button>
                <button
                  type="button"
                  className="clear-filters-btn"
                  style={{ flex: 1, marginTop: 0 }}
                  disabled={
                    !filters.repHistoryProjectId
                    && !filters.repHistoryDateFrom
                    && !filters.repHistoryDateTo
                    && !filters.repHistoryRepIds
                  }
                  onClick={clearHistoryFilter}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

/**
 * Phase 8: marker detail rendered as its own column to the right of the
 * filter sidebar. Replaces the in-sidebar Marker Detail block — the filter
 * panel keeps its filters/list state when the user opens a ticket, and the
 * detail gets a dedicated, wider canvas.
 */
function messageCountFor(openMessageCounts, recordId) {
  return Number(openMessageCounts?.[recordId] || 0);
}

function TicketMessagesSection({ ticket, canEdit, openMessageCounts, onThreadClosed, onThreadCreated, fieldReps }) {
  const count = messageCountFor(openMessageCounts, ticket.recordId);
  return (
    <CollapsibleSection title="Messages" defaultOpen={false} count={count}>
      <TicketChatPanel
        variant="embedded"
        tickets={[{
          recordId: ticket.recordId,
          siteName: ticket.siteName || '',
          itemName: ticket.itemName || '',
          openCount: count,
          relatedFieldRepId: ticket.relatedFieldRepId || null,
          fieldRepName: ticket.fieldRepName || '',
        }]}
        fieldReps={fieldReps}
        canEdit={canEdit}
        onThreadClosed={onThreadClosed}
        onThreadCreated={onThreadCreated}
      />
    </CollapsibleSection>
  );
}

export function MarkerDetailColumn({
  selectedMarker,
  fieldReps,
  canEdit,
  onClearSelection,
  onTicketUpdated,
  onSelectionChange,
  onOpenFilters,
  openMessageCounts,
  onThreadClosed,
  onThreadCreated,
}) {
  if (!selectedMarker) return null;

  const isStack = selectedMarker._isStack;
  const isRep = selectedMarker._layerType === 'fieldRep';
  const isDispatched = (selectedMarker.activeDsps ?? 0) >= 1;
  const stackCount = selectedMarker._stackMembers?.length || 0;
  const isRepStack = isStack && selectedMarker._stackMembers?.[0]?._layerType === 'fieldRep';

  let title;
  if (isStack && selectedMarker._isMultiSelect) {
    title = `${stackCount} Tickets selected`;
  } else if (isStack) {
    title = `${stackCount} ${isRepStack ? 'Reps' : 'Tickets'} at Location`;
  } else if (isRep) {
    title = 'Field Rep';
  } else {
    title = 'Ticket Detail';
  }

  return (
    <div className="coord-detail-column">
      <div className="coord-sheet-handle" aria-hidden="true" />
      <div className="coord-detail-header">
        <h3>{title}</h3>
        <div className="coord-detail-header-actions">
          {onOpenFilters && (
            <button
              type="button"
              className="coord-sheet-filters-btn"
              onClick={onOpenFilters}
            >
              Filters
            </button>
          )}
          <button className="coord-detail-close" onClick={onClearSelection} aria-label="Close">×</button>
        </div>
      </div>
      <div className="coord-detail-body">
        {isStack ? (
          <StackDetail
            members={selectedMarker._stackMembers}
            fieldReps={fieldReps}
            canEdit={canEdit}
            onTicketUpdated={onTicketUpdated}
            onSelectionChange={onSelectionChange}
            openMessageCounts={openMessageCounts}
            onThreadClosed={onThreadClosed}
            onThreadCreated={onThreadCreated}
          />
        ) : isRep ? (
          <RepDetail rep={selectedMarker} />
        ) : canEdit && !isDispatched ? (
          <EditableTicketDetail
            key={selectedMarker.recordId}
            ticket={selectedMarker}
            fieldReps={fieldReps}
            onTicketUpdated={onTicketUpdated}
            openMessageCounts={openMessageCounts}
            onThreadClosed={onThreadClosed}
            onThreadCreated={onThreadCreated}
          />
        ) : (
          <TicketDetail
            ticket={selectedMarker}
            canEdit={canEdit}
            isDispatched={isDispatched}
            onTicketUpdated={onTicketUpdated}
            fieldReps={fieldReps}
            openMessageCounts={openMessageCounts}
            onThreadClosed={onThreadClosed}
            onThreadCreated={onThreadCreated}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Multi-select dropdown with checkmarks.
 * selected: null = all selected, Set = specific selections
 */
function MultiSelectDropdown({ label, selected, options, onChange, renderOption }) {
  const displayOption = renderOption || (opt => opt);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const allSelected = selected === null;
  const count = allSelected ? options.length : selected.size;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (opt) => {
    let next;
    if (allSelected) {
      // Deselect one: create set with all except this
      next = new Set(options.filter(o => o !== opt));
    } else {
      next = new Set(selected);
      if (next.has(opt)) next.delete(opt); else next.add(opt);
    }
    // If all are selected, go back to null
    onChange(next.size === options.length ? null : (next.size === 0 ? new Set() : next));
  };

  const selectAll = () => onChange(null);
  const unselectAll = () => onChange(new Set());

  return (
    <div className="filter-row" ref={ref}>
      <label className="filter-label">{label}</label>
      <button className="multiselect-trigger" onClick={() => setOpen(!open)}>
        <span>{allSelected ? `All (${options.length})` : `${count} of ${options.length}`}</span>
        <span className="multiselect-arrow">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="multiselect-dropdown">
          <div className="multiselect-options">
            {options.map(opt => {
              const checked = allSelected || selected.has(opt);
              return (
                <label key={opt} className="multiselect-option">
                  <input type="checkbox" checked={checked} onChange={() => toggle(opt)} />
                  <span>{displayOption(opt)}</span>
                </label>
              );
            })}
          </div>
          <div className="multiselect-actions">
            <button onClick={selectAll}>Select All</button>
            <button onClick={unselectAll}>Unselect All</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ticketIsDispatched(ticket) {
  return (ticket.activeDsps ?? 0) >= 1;
}

function StackDetail({ members, fieldReps = [], canEdit = false, onTicketUpdated, onSelectionChange, openMessageCounts, onThreadClosed, onThreadCreated }) {
  const [expandedId, setExpandedId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionProgress, setActionProgress] = useState(null);
  const [actionError, setActionError] = useState(null);
  const isRepStack = members[0]?._layerType === 'fieldRep';

  const memberKey = (member, i) => (
    isRepStack ? (member.repId ?? member.id ?? i) : (member.recordId ?? member.id ?? i)
  );

  const dispatchedMembers = isRepStack ? [] : members.filter(ticketIsDispatched);
  const hasDispatched = dispatchedMembers.length > 0;
  const canBulkEdit = canEdit && !isRepStack && members.length > 1 && !hasDispatched;
  const unassignedCount = isRepStack ? 0 : members.filter(m => !m.relatedFieldRepId).length;
  const canDispatch = canEdit && !isRepStack && !hasDispatched && unassignedCount === 0 && members.length > 1;
  const canRecall = canEdit && !isRepStack && hasDispatched;

  const removeMember = (member) => {
    if (!onSelectionChange) return;
    const next = members.filter(m => m.recordId !== member.recordId);
    onSelectionChange(next);
  };

  const runBulkAction = async (action) => {
    const targets = action === 'recall'
      ? dispatchedMembers
      : members.filter(m => !ticketIsDispatched(m) && m.relatedFieldRepId);
    if (targets.length === 0) return;
    setActionBusy(true);
    setActionError(null);
    setActionProgress(`${action === 'recall' ? 'Recalling' : 'Dispatching'} 1 of ${targets.length}…`);
    const results = await dispatchTicketActions(
      targets.map(t => t.recordId),
      action,
      {
        onProgress: ({ index, total }) => {
          setActionProgress(`${action === 'recall' ? 'Recalling' : 'Dispatching'} ${index + 1} of ${total}…`);
        },
        onTicketUpdated,
      }
    );
    const failed = results.filter(r => !r.ok);
    setActionBusy(false);
    setActionProgress(null);
    setConfirmAction(null);
    if (failed.length > 0) {
      setActionError(`${failed.length} of ${results.length} failed: ${failed.map(f => `#${f.recordId} ${f.error}`).join('; ')}`);
    }
  };

  if (bulkOpen && canBulkEdit) {
    return (
      <BulkEditForm
        members={members}
        fieldReps={fieldReps}
        onTicketUpdated={onTicketUpdated}
        onBack={() => setBulkOpen(false)}
      />
    );
  }

  return (
    <div className="stack-detail">
      {canEdit && !isRepStack && members.length > 1 && (
        <div className="stack-bulk-bar">
          {hasDispatched && (
            <div className="stack-bulk-notice">
              Can&apos;t bulk edit — {dispatchedMembers.length} of {members.length}
              {dispatchedMembers.length === 1 ? ' is' : ' are'} dispatched.
              Recall {dispatchedMembers.length === 1 ? 'it' : 'them'} first, or remove from the selection.
            </div>
          )}
          <div className="stack-bulk-actions">
            <button
              type="button"
              className="action-btn action-btn-primary"
              disabled={!canBulkEdit}
              title={hasDispatched ? 'Recall dispatched tickets before bulk editing' : ''}
              onClick={() => setBulkOpen(true)}
            >
              Bulk Edit
            </button>
            {canRecall && (
              <button
                type="button"
                className="action-btn action-btn-danger"
                disabled={actionBusy}
                onClick={() => setConfirmAction('recall')}
              >
                Recall {dispatchedMembers.length}
              </button>
            )}
            {!hasDispatched && (
              <button
                type="button"
                className="action-btn action-btn-primary"
                disabled={!canDispatch || actionBusy}
                title={unassignedCount > 0 ? `Assign a field rep to all tickets first (${unassignedCount} unassigned)` : ''}
                onClick={() => setConfirmAction('dispatch')}
              >
                Dispatch {members.length}
              </button>
            )}
          </div>
          {actionProgress && <div className="stack-bulk-progress">{actionProgress}</div>}
          {actionError && <div className="edit-error">{actionError}</div>}
        </div>
      )}
      <div className="stack-list">
        {members.map((member, i) => {
          const key = memberKey(member, i);
          const isExpanded = expandedId === key;
          const isDispatched = ticketIsDispatched(member);
          return (
            <div key={key} className={`stack-item ${isExpanded ? 'expanded' : ''}`}>
              <div
                className="stack-item-header"
                onClick={() => setExpandedId(isExpanded ? null : key)}
                style={{ cursor: 'pointer' }}
              >
                <span className="stack-expand-icon">{isExpanded ? '▾' : '▸'}</span>
                {isRepStack ? (
                  <>
                    <span className="stack-item-site">{member.name || 'Unknown Rep'}</span>
                    {renderStars(member.rating)}
                    {member.otrResAbdnHtml && <ComplianceBar html={member.otrResAbdnHtml} />}
                  </>
                ) : (
                  <>
                    <a
                      className="stack-item-site"
                      href={ticketQbUrl(member.recordId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open ticket in Quickbase"
                      onClick={e => e.stopPropagation()}
                    >
                      {member.siteName || 'Unknown Site'}
                    </a>
                    <span className={`stack-item-status ${isDispatched ? 'dispatched' : (member.fieldRepName || member.relatedFieldRepId ? 'assigned' : 'unassigned')}`}>
                      {isDispatched ? 'Dispatched' : (member.fieldRepName || member.relatedFieldRepId ? 'Assigned' : 'Unassigned')}
                    </span>
                    {onSelectionChange && members.length > 1 && (
                      <button
                        type="button"
                        className="stack-item-remove"
                        title="Remove from selection"
                        aria-label="Remove from selection"
                        onClick={(e) => { e.stopPropagation(); removeMember(member); }}
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </div>
              {!isExpanded && (
                <div className="stack-item-details">
                  {isRepStack ? (
                    <>
                      <span>#{member.repId}</span>
                      <span>{member.phone || member.paymentMethod || '—'}</span>
                    </>
                  ) : (
                    <>
                      <span>#{member.recordId}</span>
                      <span>{member.itemName}</span>
                      <span>{member.fieldRepName || (member.relatedFieldRepId ? `Rep #${member.relatedFieldRepId}` : 'Unassigned')}</span>
                    </>
                  )}
                </div>
              )}
              {!isExpanded && !isRepStack && member.checkIn && (
                <div className="stack-checkin-hint">
                  ✓ {[member.checkIn.repName || 'Checked in', member.checkIn.distanceLabel].filter(Boolean).join(' · ')}
                </div>
              )}
              {isExpanded && (
                isRepStack ? (
                  <RepDetail rep={member} />
                ) : canEdit && !isDispatched ? (
                  <EditableTicketDetail
                    key={member.recordId}
                    ticket={member}
                    fieldReps={fieldReps}
                    onTicketUpdated={onTicketUpdated}
                    openMessageCounts={openMessageCounts}
                    onThreadClosed={onThreadClosed}
                    onThreadCreated={onThreadCreated}
                  />
                ) : (
                  <TicketDetail
                    ticket={member}
                    canEdit={canEdit}
                    openMessageCounts={openMessageCounts}
                    onThreadClosed={onThreadClosed}
                    onThreadCreated={onThreadCreated}
                    isDispatched={isDispatched}
                    onTicketUpdated={onTicketUpdated}
                    fieldReps={fieldReps}
                  />
                )
              )}
            </div>
          );
        })}
      </div>
      <ActionConfirmModal
        open={!!confirmAction}
        title={confirmAction === 'recall' ? `Recall ${dispatchedMembers.length} ticket${dispatchedMembers.length === 1 ? '' : 's'}?` : `Dispatch ${members.length} tickets?`}
        message={confirmAction === 'recall'
          ? `Recall ${dispatchedMembers.length} dispatched ticket${dispatchedMembers.length === 1 ? '' : 's'}? Each webhook is sent with a short delay.`
          : `Dispatch ${members.length} tickets to their assigned reps? Each webhook is sent with a short delay.`}
        confirmLabel={confirmAction === 'recall' ? 'Recall' : 'Dispatch'}
        confirmDanger={confirmAction === 'recall'}
        busy={actionBusy}
        onCancel={() => { if (!actionBusy) setConfirmAction(null); }}
        onConfirm={() => runBulkAction(confirmAction)}
      />
    </div>
  );
}

function normalizeBulkValue(key, raw) {
  if (key === 'scheduleDate') return toDateInputValue(raw);
  if (key === 'relatedFieldRepId') return raw ?? null;
  if (raw === undefined || raw === null) return '';
  return raw;
}

function buildBulkFieldState(members) {
  const fields = {};
  for (const key of BULK_EDIT_FIELDS) {
    const raw = members.map(m => normalizeBulkValue(key, m[key]));
    const first = raw[0];
    const allSame = raw.every(v => valuesEqualish(v, first));
    const counts = new Map();
    for (const v of raw) {
      const k = v === null || v === undefined || v === '' ? '' : String(v);
      if (!counts.has(k)) counts.set(k, { value: v === '' ? (key === 'relatedFieldRepId' ? null : '') : v, count: 0 });
      counts.get(k).count += 1;
    }
    fields[key] = {
      mixed: !allSame,
      value: allSame ? first : (key === 'relatedFieldRepId' ? null : ''),
      variants: [...counts.values()].sort((a, b) => b.count - a.count),
    };
  }
  return fields;
}

function formatBulkVariant(key, value, fieldReps) {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (key === 'relatedFieldRepId') {
    const rep = findFieldRep(fieldReps, value);
    return rep ? `${rep.name} · #${rep.repId}` : `Rep #${value}`;
  }
  if (key === 'scheduleDate') return formatDatePST(value);
  if (['fixedRate', 'firstHourRate', 'hourlyRate', 'travel'].includes(key)) {
    return formatCurrency(value) || String(value);
  }
  const s = String(value);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

function MixedValueHint({ fieldKey, variants, fieldReps, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bulk-mixed">
      <button type="button" className="bulk-view-values" onClick={() => setOpen(o => !o)}>
        {open ? 'Hide values' : 'View values'}
      </button>
      {open && (
        <ul className="bulk-value-list">
          {variants.map((v, i) => (
            <li key={`${fieldKey}-${i}`}>
              <button type="button" onClick={() => onPick(v.value)}>
                <span>{formatBulkVariant(fieldKey, v.value, fieldReps)}</span>
                <span className="bulk-value-count">×{v.count}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BulkEditForm({ members, fieldReps, onTicketUpdated, onBack }) {
  const initialFields = useMemo(() => buildBulkFieldState(members), [members]);
  const [fields, setFields] = useState(initialFields);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [saveResults, setSaveResults] = useState(null);

  useEffect(() => {
    setFields(initialFields);
    setErrorMessage(null);
    setSaveResults(null);
  }, [initialFields]);

  const setField = (key, val) => {
    setFields(prev => ({ ...prev, [key]: { ...prev[key], value: val } }));
  };

  const changes = useMemo(() => {
    const out = {};
    for (const key of BULK_EDIT_FIELDS) {
      const cur = fields[key];
      const init = initialFields[key];
      if (!cur || !init) continue;
      if (!valuesEqualish(cur.value, init.value)) {
        out[key] = cur.value === '' ? null : cur.value;
      }
    }
    return out;
  }, [fields, initialFields]);
  const pendingCount = Object.keys(changes).length;

  const showShipping = members.some(m => m.partsRequired === true);
  const assigningRep = Object.prototype.hasOwnProperty.call(changes, 'relatedFieldRepId') && !!changes.relatedFieldRepId;
  const shipToMissing = assigningRep && members.some(m => (
    m.partsRequired === true
    && !m.relatedFieldRepId
    && !changes.shipToLocation
    && !m.shipToLocation
  ));

  const doSave = async () => {
    if (pendingCount === 0 || shipToMissing) return;
    if (members.some(ticketIsDispatched)) {
      setErrorMessage('Can\'t bulk edit while any selected ticket is dispatched. Recall first.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    setSaveResults(null);
    const results = [];
    for (let i = 0; i < members.length; i++) {
      const ticket = members[i];
      setProgress(`Saving ${i + 1} of ${members.length}…`);
      const originalValues = {};
      for (const key of Object.keys(changes)) {
        originalValues[key] = normalizeBulkValue(key, ticket[key]);
        if (originalValues[key] === '') originalValues[key] = null;
      }
      try {
        const result = await saveTicketChanges(
          ticket.recordId,
          changes,
          originalValues,
          ticket.dateModified,
          false
        );
        if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
        results.push({ recordId: ticket.recordId, ok: true });
      } catch (err) {
        results.push({
          recordId: ticket.recordId,
          ok: false,
          error: err.message || 'Save failed',
          hardConflict: !!err.hardConflict,
        });
      }
    }
    setSaving(false);
    setProgress(null);
    const failed = results.filter(r => !r.ok);
    setSaveResults(results);
    if (failed.length === 0) {
      onBack();
    } else {
      setErrorMessage(`${failed.length} of ${results.length} tickets failed to save.`);
    }
  };

  const fieldClass = (key) => `edit-field ${changes[key] !== undefined ? 'edit-field-changed' : ''}${fields[key]?.mixed ? ' edit-field-mixed' : ''}`;

  const renderMixed = (key) => (
    fields[key]?.mixed ? (
      <MixedValueHint
        fieldKey={key}
        variants={fields[key].variants}
        fieldReps={fieldReps}
        onPick={(val) => setField(key, val)}
      />
    ) : null
  );

  return (
    <div className="detail-content editable-detail bulk-edit-form">
      <button type="button" className="bulk-back" onClick={onBack} disabled={saving}>
        ← {members.length} tickets
      </button>
      <h4>Bulk Edit</h4>
      <p className="section-desc" style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 10px' }}>
        Only fields you change are written to every selected ticket. Mixed fields start empty — use View values to inspect or adopt one.
      </p>

      <CollapsibleSection title="Instructions" defaultOpen={false}>
        <div className={fieldClass('description')}>
          <label>Description</label>
          <div className="edit-input-wrap edit-input-wrap-textarea">
            <AutoTextarea
              value={fields.description?.value ?? ''}
              onChange={e => setField('description', e.target.value)}
            />
          </div>
          {renderMixed('description')}
        </div>
        <div className={fieldClass('siteVisitInstructions')}>
          <label>Site Visit Instructions</label>
          <div className="edit-input-wrap edit-input-wrap-textarea">
            <AutoTextarea
              value={fields.siteVisitInstructions?.value ?? ''}
              onChange={e => setField('siteVisitInstructions', e.target.value)}
            />
          </div>
          {renderMixed('siteVisitInstructions')}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Payment" defaultOpen={false}>
        <div className={fieldClass('payType')}>
          <label>Pay Type</label>
          <div className="edit-input-wrap">
            <select value={fields.payType?.value ?? ''} onChange={e => setField('payType', e.target.value)}>
              <option value="">— None —</option>
              {PAY_TYPE_CHOICES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ClearBtn show={!!fields.payType?.value} onClick={() => setField('payType', '')} />
          </div>
          {renderMixed('payType')}
        </div>
        {[
          ['fixedRate', 'Fixed Rate'],
          ['firstHourRate', 'First Hour Rate'],
          ['hourlyRate', 'Hourly Rate'],
          ['travel', 'Travel'],
          ['minHours', 'Min Hours'],
          ['expectedHours', 'Expected Hours'],
          ['maxHours', 'Max Hours'],
        ].map(([key, label]) => (
          <div key={key} className={fieldClass(key)}>
            <label>{label}</label>
            <div className="edit-input-wrap">
              <input
                type="number"
                step="any"
                value={fields[key]?.value ?? ''}
                onChange={e => setField(key, e.target.value)}
              />
              <ClearBtn
                show={fields[key]?.value !== '' && fields[key]?.value != null}
                onClick={() => setField(key, '')}
              />
            </div>
            {renderMixed(key)}
          </div>
        ))}
      </CollapsibleSection>

      <BulkOpportunityInvites members={members} fieldReps={fieldReps} />

      <CollapsibleSection title="Scheduling">
        <div className={fieldClass('scheduleDate')}>
          <label>Schedule Date</label>
          <div className="edit-input-wrap">
            <input
              type="date"
              value={fields.scheduleDate?.value ?? ''}
              onChange={e => setField('scheduleDate', e.target.value)}
            />
            <ClearBtn show={!!fields.scheduleDate?.value} onClick={() => setField('scheduleDate', '')} />
          </div>
          {renderMixed('scheduleDate')}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Field Rep">
        <div className={fieldClass('relatedFieldRepId')}>
          <RepSearchSelect
            value={fields.relatedFieldRepId?.value ?? null}
            onChange={(repId) => setField('relatedFieldRepId', repId)}
            fieldReps={fieldReps}
            nearbyJson={null}
          />
          {renderMixed('relatedFieldRepId')}
        </div>
      </CollapsibleSection>

      {showShipping && (
        <CollapsibleSection title="Shipping" defaultOpen={shipToMissing}>
          <div className={`${fieldClass('shipToLocation')}${shipToMissing ? ' edit-field-required' : ''}`}>
            <label>Ship To{assigningRep && <span className="edit-required-mark">*</span>}</label>
            <div className="edit-input-wrap">
              <select
                value={fields.shipToLocation?.value ?? ''}
                onChange={e => setField('shipToLocation', e.target.value)}
              >
                <option value="">— None —</option>
                {SHIP_TO_CHOICES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ClearBtn show={!!fields.shipToLocation?.value} onClick={() => setField('shipToLocation', '')} />
            </div>
            {renderMixed('shipToLocation')}
            {shipToMissing && (
              <div className="edit-field-hint">Required when assigning a rep to a parts-required ticket.</div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {errorMessage && <div className="edit-error">{errorMessage}</div>}
      {saveResults && saveResults.some(r => !r.ok) && (
        <ul className="bulk-save-errors">
          {saveResults.filter(r => !r.ok).map(r => (
            <li key={r.recordId}>#{r.recordId}: {r.error}</li>
          ))}
        </ul>
      )}
      {progress && <div className="stack-bulk-progress">{progress}</div>}

      <div className="edit-footer">
        <button className="edit-cancel" onClick={onBack} disabled={saving}>
          Cancel
        </button>
        <button
          className="edit-save"
          onClick={doSave}
          disabled={saving || pendingCount === 0 || shipToMissing}
          title={shipToMissing ? 'Select a Ship To location before saving' : ''}
        >
          {saving ? 'Saving…' : `Save to ${members.length} Tickets (${pendingCount})`}
        </button>
      </div>
    </div>
  );
}

function findFieldRep(fieldReps, repId) {
  if (repId == null || repId === '') return null;
  return fieldReps.find(r => valuesEqualish(r.repId, repId)) || null;
}

function TicketDetail({ ticket, canEdit = false, isDispatched = false, onTicketUpdated, fieldReps = [], openMessageCounts, onThreadClosed, onThreadCreated }) {
  const assignedRep = findFieldRep(fieldReps, ticket.relatedFieldRepId);
  const qbUrl = ticketQbUrl(ticket.recordId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const doRecall = async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const result = await dispatchTicketAction(ticket.recordId, 'recall');
      if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
      setConfirmOpen(false);
    } catch (err) {
      setErrorMessage(err.message || 'Recall failed');
    } finally {
      setBusy(false);
    }
  };
  const partsRequired = ticket.partsRequired === true;
  return (
    <div className="detail-content">
      <h4>
        <a href={qbUrl} target="_blank" rel="noopener noreferrer" title="Open ticket in Quickbase">
          {ticket.siteName || 'Unknown Site'}
        </a>
      </h4>
      <CheckInCard checkIn={ticket.checkIn} />
      <div className="detail-field">
        <span>Record ID:</span> {ticket.recordId} <a href={qbUrl} target="_blank" rel="noopener noreferrer" className="inline-link">QB↗</a>
        {ticket.fnWoId && <span style={{ marginLeft: 8, color: '#6b7280' }}>· FN WO# {ticket.fnWoId}</span>}
      </div>
      <div className="detail-field"><span>Project:</span> {ticket.projectName || '—'}</div>
      <div className="detail-field"><span>Service / Item:</span> {ticket.serviceType || '—'} / {ticket.itemName || '—'}</div>
      {ticket.appTemplateName && (
        <div className="detail-field"><span>App Template:</span> {ticket.appTemplateName}</div>
      )}
      <div className="detail-field"><span>Status:</span> {ticket.status || '—'}</div>
      <div className="detail-field"><span>Location:</span> {ticket.city}{ticket.state ? `, ${ticket.state}` : ''}</div>
      <div className="detail-field detail-description">
        <span>Description:</span>
        <p>{ticket.description || '—'}</p>
      </div>

      {assignedRep ? (
        <CollapsibleSection title="Field Rep Info">
          <div className="detail-field"><span>Name:</span> {assignedRep.name || '—'}</div>
          <div className="detail-field"><span>Rep ID:</span> {assignedRep.repId}</div>
          <RepDetail rep={assignedRep} hideName />
        </CollapsibleSection>
      ) : ticket.fieldRepName || ticket.relatedFieldRepId ? (
        <div className="detail-field">
          <span>Field Rep:</span>{' '}
          {ticket.fieldRepName || `Rep #${ticket.relatedFieldRepId}`}
          {ticket.relatedFieldRepId && ticket.fieldRepName ? (
            <span style={{ color: '#6b7280' }}> · #{ticket.relatedFieldRepId}</span>
          ) : null}
        </div>
      ) : (
        <div className="detail-field">
          <span>Field Rep:</span> <em style={{ color: '#d97706' }}>Unassigned</em>
        </div>
      )}

      <CollapsibleSection title="Scheduling">
        <div className="detail-field"><span>Schedule Date:</span> {formatDatePST(ticket.scheduleDate)}</div>
        <div className="detail-field"><span>Start Date:</span> {formatDatePST(ticket.startDate)}</div>
        <div className="detail-field"><span>Due Date:</span> {formatDatePST(ticket.dueDate)}</div>
      </CollapsibleSection>

      <TicketMessagesSection
        ticket={ticket}
        canEdit={canEdit}
        openMessageCounts={openMessageCounts}
        onThreadClosed={onThreadClosed}
        onThreadCreated={onThreadCreated}
        fieldReps={fieldReps}
      />

      <CollapsibleSection title="Instructions" defaultOpen={false}>
        {ticket.description && (
          <div className="detail-field detail-description">
            <span>Description:</span>
            <p>{ticket.description}</p>
          </div>
        )}
        {ticket.toolsRequired && (
          <div className="detail-field detail-description">
            <span>Required Tools:</span>
            <p>{ticket.toolsRequired}</p>
          </div>
        )}
        {ticket.technicalScope && (
          <div className="detail-field detail-description">
            <span>Technical Scope:</span>
            <p>{ticket.technicalScope}</p>
          </div>
        )}
        {ticket.siteVisitInstructions && (
          <div className="detail-field detail-description">
            <span>Site Visit Instructions:</span>
            <p>{ticket.siteVisitInstructions}</p>
          </div>
        )}
        {!ticket.description && !ticket.toolsRequired && !ticket.technicalScope && !ticket.siteVisitInstructions && (
          <div className="section-desc" style={{ fontSize: 11, color: '#6b7280' }}>
            No instructions on file.
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Payment" defaultOpen={false}>
        <div className="detail-field"><span>Pay Type:</span> {ticket.payType || '—'}</div>
        {[
          ['Fixed Rate', formatCurrency(ticket.fixedRate)],
          ['First Hour Rate', formatCurrency(ticket.firstHourRate)],
          ['Hourly Rate', formatCurrency(ticket.hourlyRate)],
          ['Travel', formatCurrency(ticket.travel)],
          ['Min Hours', ticket.minHours],
          ['Expected Hours', ticket.expectedHours],
          ['Max Hours', ticket.maxHours],
        ].filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—').map(([label, val]) => (
          <div key={label} className="detail-field"><span>{label}:</span> {val}</div>
        ))}
      </CollapsibleSection>

      {partsRequired && (
        <CollapsibleSection title="Shipping" defaultOpen={false}>
          {ticket.shipToLocation && (
            <div className="detail-field"><span>Ship To:</span> {ticket.shipToLocation}</div>
          )}
          <PartsTrackingDisplay ticket={ticket} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Field Nation" defaultOpen={false}>
        <FieldNationSection ticket={ticket} canEdit={canEdit} onTicketUpdated={onTicketUpdated} />
      </CollapsibleSection>

      <CollapsibleSection title="Confirmations" defaultOpen={false}>
        <ConfirmationsSection ticket={ticket} canEdit={canEdit} onTicketUpdated={onTicketUpdated} />
      </CollapsibleSection>

      {canEdit && isDispatched && (
        <CollapsibleSection title="Actions">
          <p className="section-desc" style={{ fontSize: 11, color: '#6b7280', margin: '4px 0' }}>
            Ticket is dispatched. To change anything, recall first — then edit and dispatch again.
          </p>
          <div className="action-buttons">
            <button
              className="action-btn action-btn-danger"
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
            >
              {busy ? 'Recalling…' : 'Recall'}
            </button>
          </div>
          {errorMessage && <div className="edit-error">{errorMessage}</div>}
        </CollapsibleSection>
      )}

      <a href={qbUrl} target="_blank" rel="noopener noreferrer" className="detail-link">
        View in QuickBase
      </a>

      <ActionConfirmModal
        open={confirmOpen}
        title="Recall this ticket?"
        message={ticket.fieldRepName
          ? `Recall this ticket from ${ticket.fieldRepName}? The rep will be notified and the ticket will return to the assigned-not-dispatched state.`
          : 'Recall this ticket? It will return to the assigned-not-dispatched state.'}
        confirmLabel="Recall"
        confirmDanger
        busy={busy}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={doRecall}
      />
    </div>
  );
}

function RepSearchSelect({ value, onChange, fieldReps, nearbyJson }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Parse FID 1711 nearby JSON once: { repId → distanceMiles }
  const nearbyMap = useMemo(() => {
    if (!nearbyJson) return new Map();
    try {
      const arr = JSON.parse(nearbyJson);
      const m = new Map();
      arr.forEach(entry => m.set(parseInt(entry.id, 10), parseFloat(entry.dist)));
      return m;
    } catch {
      return new Map();
    }
  }, [nearbyJson]);

  // Filter + sort options (nearby first, then alphabetical)
  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = fieldReps.filter(r => {
      if (!q) return true;
      return (r.name || '').toLowerCase().includes(q) || String(r.repId).includes(q);
    });
    result.sort((a, b) => {
      const aDist = nearbyMap.get(a.repId);
      const bDist = nearbyMap.get(b.repId);
      if (aDist !== undefined && bDist !== undefined) return aDist - bDist;
      if (aDist !== undefined) return -1;
      if (bDist !== undefined) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return result;
  }, [fieldReps, query, nearbyMap]);

  const visibleOptions = options.slice(0, 50);
  const truncated = options.length - visibleOptions.length;

  const selectedRep = findFieldRep(fieldReps, value);
  const displayText = selectedRep
    ? `${selectedRep.name} · #${selectedRep.repId}`
    : (value != null && value !== '' ? `Rep #${value}` : '');

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (repId) => {
    onChange(repId);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  return (
    <div className="rep-search" ref={containerRef}>
      <div className="rep-search-input-row">
        <input
          ref={inputRef}
          type="text"
          value={open ? query : displayText}
          placeholder={selectedRep ? '' : 'Search by name or rep ID…'}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onKeyDown={e => {
            if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
          }}
        />
        <ClearBtn show={value != null} onClick={() => onChange(null)} />
      </div>
      {open && (
        <div className="rep-search-dropdown">
          {visibleOptions.length === 0 ? (
            <div className="rep-search-empty">No matches</div>
          ) : (
            <>
              {visibleOptions.map(r => {
                const dist = nearbyMap.get(r.repId) ?? nearbyMap.get(parseInt(r.repId, 10));
                const isSelected = valuesEqualish(r.repId, value);
                return (
                  <button
                    type="button"
                    key={r.repId}
                    className={`rep-search-option${isSelected ? ' selected' : ''}`}
                    onMouseDown={e => { e.preventDefault(); handleSelect(r.repId); }}
                  >
                    <span className="rep-search-name">{r.name || '(no name)'}</span>
                    <span className="rep-search-id">#{r.repId}</span>
                    {dist !== undefined && !isNaN(dist) && (
                      <span className="rep-search-dist">{dist.toFixed(1)} mi</span>
                    )}
                  </button>
                );
              })}
              {truncated > 0 && (
                <div className="rep-search-more">{truncated} more — type to narrow</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, open: controlledOpen, onOpenChange, count, children }) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const toggle = () => {
    const next = !open;
    if (!isControlled) setInternalOpen(next);
    if (onOpenChange) onOpenChange(next);
  };
  return (
    <>
      <div
        className="detail-section-header detail-section-header-collapsible"
        onClick={toggle}
      >
        <span className="section-toggle">{open ? '▾' : '▸'}</span>
        <span className="section-title">{title}</span>
        {count != null && <span className="section-count">({count})</span>}
      </div>
      {open && children}
    </>
  );
}

// Textarea that grows to fit its content up to maxHeight (px), then scrolls.
function AutoTextarea({ value, onChange, minHeight = 60, maxHeight = 260, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, minHeight, maxHeight]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      style={{ minHeight, maxHeight, resize: 'none' }}
      {...rest}
    />
  );
}

function ClearBtn({ show, onClick }) {
  if (!show) return null;
  return (
    <button
      type="button"
      className="edit-clear-btn"
      onClick={onClick}
      title="Clear field"
      aria-label="Clear field"
    >
      ×
    </button>
  );
}

function SoftWarningBanner({ warning, onDismiss }) {
  if (!warning) return null;
  return (
    <div className="soft-warning-banner">
      <strong>Heads up:</strong> {warning.message}
      <div className="soft-warning-meta">
        Last edited by {warning.lastModifiedBy} at {formatDatePST(warning.lastModifiedAt)}
      </div>
      {onDismiss && <button className="soft-warning-dismiss" onClick={onDismiss}>×</button>}
    </div>
  );
}

function ConflictPanel({ conflict, onCancel, onSubmitAnyway, submitting }) {
  if (!conflict) return null;
  const fields = Object.entries(conflict.conflictedFields || {});
  return (
    <div className="conflict-overlay">
      <div className="conflict-panel">
        <h3>⚠ Conflict Detected</h3>
        <p>
          This ticket was modified by <strong>{conflict.lastModifiedBy}</strong>
          {' '}at {formatDatePST(conflict.lastModifiedAt)}.
        </p>
        <div className="conflict-fields">
          {fields.map(([key, diff]) => (
            <div key={key} className="conflict-field">
              <div className="conflict-field-name">{EDITABLE_FIELD_LABELS[key] || key}</div>
              <div className="conflict-field-row"><span>Current (QB):</span> <code>{String(diff.current ?? '—')}</code></div>
              <div className="conflict-field-row"><span>Your starting value:</span> <code>{String(diff.original ?? '—')}</code></div>
              <div className="conflict-field-row"><span>Your change:</span> <code>{String(diff.yourChange ?? '—')}</code></div>
            </div>
          ))}
        </div>
        <div className="conflict-actions">
          <button onClick={onCancel} disabled={submitting}>Cancel</button>
          <button onClick={onSubmitAnyway} disabled={submitting} className="conflict-submit">
            {submitting ? 'Saving…' : 'Submit Anyway'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionConfirmModal({ open, title, message, confirmLabel, confirmDanger, busy, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="conflict-overlay">
      <div className="conflict-panel">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="conflict-actions">
          <button onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={confirmDanger ? 'action-confirm-danger' : 'conflict-submit'}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Read-only parts tracking lifecycle. Renders inside the Shipping section
 * when partsRequired is true. Hides individual rows when the value is empty
 * so unset fields don't clutter the display.
 */
function PartsTrackingDisplay({ ticket }) {
  const rows = [
    ['Carrier', ticket.carrier],
    ['Tracking #', ticket.trackingNumber, ticket.getTrackingUrl],
    ['Tracking Entered', formatDatePST(ticket.trackingEntered)],
    ['Parts ETA', formatDatePST(ticket.partsEta)],
    ['Package Status', ticket.packageStatus],
    ['Delivery Date', formatDatePST(ticket.packageDeliveryDate)],
  ].filter(([, v]) => v && v !== '—');

  const isDelivered = ticket.delivered === true;

  if (rows.length === 0 && !isDelivered) return null;

  return (
    <>
      {rows.map(([label, val, url]) => (
        <div key={label} className="detail-field">
          <span>{label}:</span>{' '}
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="inline-link">{val} ↗</a>
          ) : (
            val
          )}
        </div>
      ))}
      {isDelivered && (
        <div className="detail-field"><span>Delivered:</span> ✓</div>
      )}
    </>
  );
}

/**
 * Field Nation section. Shows the Platform Link when present, otherwise (and
 * only when the user can edit) a Create FN Workorder button. Button is
 * disabled when fnReadiness doesn't include "ready" — same gate the
 * opportunity panel uses. After the user clicks it, the backend triggers QB
 * automation and polls for the resulting link for up to 15s.
 */
function FieldNationSection({ ticket, canEdit, onTicketUpdated }) {
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const fnReady = (ticket.fnReadiness || '').toString().toLowerCase().includes('ready');
  const hasLink = !!ticket.platformLink;

  const handleCreate = async () => {
    setCreating(true);
    setErrorMessage(null);
    setPending(false);
    try {
      const result = await createFnWorkorder(ticket.recordId);
      if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
      if (result.pending) setPending(true);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to create FN workorder');
    } finally {
      setCreating(false);
    }
  };

  if (hasLink) {
    return (
      <div className="detail-field">
        <span>Platform Link:</span>{' '}
        <a href={ticket.platformLink} target="_blank" rel="noopener noreferrer" className="inline-link">
          Open in Field Nation ↗
        </a>
      </div>
    );
  }

  if (!canEdit) {
    return <div className="detail-field"><span>Platform Link:</span> —</div>;
  }

  return (
    <>
      <div className="action-buttons">
        <button
          className="action-btn"
          onClick={handleCreate}
          disabled={!fnReady || creating}
          title={!fnReady ? 'FN not ready for this ticket' : ''}
        >
          {creating ? 'Creating… (up to 15s)' : 'Create FN Workorder'}
        </button>
      </div>
      {pending && (
        <div className="fn-pending-note">
          Workorder triggered. The Platform Link will appear shortly — refresh in a minute if it doesn't show up.
        </div>
      )}
      {errorMessage && <div className="edit-error">{errorMessage}</div>}
    </>
  );
}

/**
 * Confirmations section. Always shows Last Confirm (FID 467) inline plus a
 * "View All" button that opens a modal listing every confirmation related to
 * this ticket (FK = FID 10 on the confirmations table). The modal also has
 * an "Add Confirmation" mode for creating a new entry.
 */
function ConfirmationsSection({ ticket, canEdit, onTicketUpdated }) {
  const [modalState, setModalState] = useState(null); // null | 'list' | 'add'
  return (
    <>
      <div className="detail-field"><span>Last Confirm:</span> {ticket.lastConfirm || '—'}</div>
      <div className="action-buttons">
        <button className="action-btn" onClick={() => setModalState('list')}>
          View All Confirmations
        </button>
        {canEdit && (
          <button className="action-btn action-btn-primary" onClick={() => setModalState('add')}>
            + Add Confirmation
          </button>
        )}
      </div>
      {modalState && (
        <ConfirmationsModal
          ticket={ticket}
          canEdit={canEdit}
          initialMode={modalState}
          onClose={() => setModalState(null)}
          onTicketUpdated={onTicketUpdated}
        />
      )}
    </>
  );
}

function ConfirmationsModal({ ticket, canEdit, initialMode = 'list', onClose, onTicketUpdated }) {
  const [mode, setMode] = useState(initialMode); // 'list' | 'add'
  const [loading, setLoading] = useState(true);
  const [confirmations, setConfirmations] = useState([]);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchTicketConfirmations(ticket.recordId)
      .then(data => { if (!cancelled) setConfirmations(data.confirmations || []); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticket.recordId, refreshKey]);

  const formatUser = (u) => {
    if (!u) return '—';
    if (typeof u === 'object') return u.name || u.email || u.id || '—';
    return String(u);
  };

  const handleAdded = (updatedTicket) => {
    if (updatedTicket && onTicketUpdated) onTicketUpdated(updatedTicket);
    // Entered via "+ Add Confirmation" → just close. Entered via "View All"
    // and switched to add → go back to the list with the new entry visible.
    if (initialMode === 'add') {
      onClose();
    } else {
      setMode('list');
      setRefreshKey(k => k + 1);
    }
  };

  return (
    <div className="conflict-overlay" onClick={onClose}>
      <div className="conflict-panel confirmations-panel" onClick={e => e.stopPropagation()}>
        <div className="confirmations-header">
          <h3 style={{ margin: 0 }}>
            {mode === 'add' ? 'Add Confirmation' : 'Confirmations'} — {ticket.siteName || `Record ${ticket.recordId}`}
          </h3>
          {mode === 'list' && canEdit && (
            <button className="action-btn action-btn-primary" onClick={() => setMode('add')}>
              + Add Confirmation
            </button>
          )}
        </div>

        {mode === 'list' && (
          <>
            {loading && <p>Loading…</p>}
            {error && <div className="edit-error">{error}</div>}
            {!loading && !error && confirmations.length === 0 && (
              <p>No confirmations on file for this ticket.</p>
            )}
            {!loading && confirmations.map(c => (
              <div key={c.recordId} className="confirmation-row">
                <div className="confirmation-meta">
                  <strong>{c.type || 'Confirmation'}</strong>
                  <span className="confirmation-when">
                    {formatDatePST(c.confirmedTime || c.dateCreated)}
                  </span>
                </div>
                <div className="confirmation-by">by {formatUser(c.confirmedBy)}</div>
                {c.notes && <div className="confirmation-notes">{c.notes}</div>}
              </div>
            ))}
            <div className="conflict-actions">
              <button onClick={onClose}>Close</button>
            </div>
          </>
        )}

        {mode === 'add' && (
          <AddConfirmationForm
            ticket={ticket}
            onCancel={() => setMode('list')}
            onAdded={handleAdded}
          />
        )}
      </div>
    </div>
  );
}

function AddConfirmationForm({ ticket, onCancel, onAdded }) {
  const [type, setType] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmedTime, setConfirmedTime] = useState(() => {
    // datetime-local needs YYYY-MM-DDTHH:mm in local time
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!type) { setError('Type is required.'); return; }
    if (!notes.trim()) { setError('Notes are required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const result = await addTicketConfirmation(ticket.recordId, {
        type,
        notes,
        confirmedTime: new Date(confirmedTime).toISOString(),
      });
      onAdded(result.ticket);
    } catch (err) {
      setError(err.message || 'Failed to add confirmation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="add-confirmation-form">
      <label>Type</label>
      <select value={type} onChange={e => setType(e.target.value)} disabled={submitting}>
        <option value="">— Select Type —</option>
        {CONFIRMATION_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>

      <label>Notes</label>
      <textarea
        rows={5}
        value={notes}
        onChange={e => setNotes(e.target.value)}
        disabled={submitting}
        placeholder="What happened? Who did you talk to? Next steps?"
      />

      <label>Confirmed Time</label>
      <input
        type="datetime-local"
        value={confirmedTime}
        onChange={e => setConfirmedTime(e.target.value)}
        disabled={submitting}
      />

      {error && <div className="edit-error">{error}</div>}

      <div className="conflict-actions">
        <button onClick={onCancel} disabled={submitting}>Cancel</button>
        <button onClick={submit} disabled={submitting} className="conflict-submit">
          {submitting ? 'Saving…' : 'Add Confirmation'}
        </button>
      </div>
    </div>
  );
}

function EditableTicketDetail({ ticket, fieldReps, onTicketUpdated, openMessageCounts, onThreadClosed, onThreadCreated }) {
  const qbUrl = ticketQbUrl(ticket.recordId);

  // Snapshot of the form's starting values — never mutated, used as `originalValues` on save
  const initialValues = useMemo(() => ({
    scheduleDate: toDateInputValue(ticket.scheduleDate),
    relatedFieldRepId: ticket.relatedFieldRepId ?? null,
    payType: ticket.payType ?? '',
    fixedRate: ticket.fixedRate ?? '',
    firstHourRate: ticket.firstHourRate ?? '',
    hourlyRate: ticket.hourlyRate ?? '',
    travel: ticket.travel ?? '',
    minHours: ticket.minHours ?? '',
    expectedHours: ticket.expectedHours ?? '',
    maxHours: ticket.maxHours ?? '',
    shipToLocation: ticket.shipToLocation ?? '',
    description: ticket.description ?? '',
    siteVisitInstructions: ticket.siteVisitInstructions ?? '',
  }), [ticket]);

  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [softWarning, setSoftWarning] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [dispatchConfirmOpen, setDispatchConfirmOpen] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [shippingOpen, setShippingOpen] = useState(false);

  // Reset state if the ticket changes underneath us
  useEffect(() => {
    setValues(initialValues);
    setConflict(null);
    setSoftWarning(null);
    setErrorMessage(null);
    setShippingOpen(false);
  }, [initialValues]);

  const setField = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  };

  // Compute changes (only fields that differ from the snapshot)
  const changes = useMemo(() => {
    const out = {};
    for (const k of Object.keys(initialValues)) {
      if (!valuesEqualish(values[k], initialValues[k])) {
        out[k] = values[k] === '' ? null : values[k];
      }
    }
    return out;
  }, [values, initialValues]);
  const pendingCount = Object.keys(changes).length;

  const partsRequired = ticket.partsRequired === true;

  // When a rep is being assigned to a previously-unassigned, parts-required
  // ticket, Ship To becomes mandatory. `initialValues.relatedFieldRepId` is
  // the snapshot of how the ticket arrived, so this fires only on the
  // unassigned → assigned transition (not when editing an already-assigned one).
  const assigningRep = !initialValues.relatedFieldRepId && !!values.relatedFieldRepId;
  const shipToRequired = partsRequired && assigningRep;
  const shipToMissing = shipToRequired && !values.shipToLocation;

  // Auto-expand the Shipping section the moment Ship To becomes required, so
  // the coordinator can't miss it.
  useEffect(() => {
    if (shipToRequired) setShippingOpen(true);
  }, [shipToRequired]);

  const doSave = async (force = false) => {
    if (shipToMissing) {
      setShippingOpen(true);
      setErrorMessage('Ship To is required when assigning a rep to a parts-required ticket.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const result = await saveTicketChanges(
        ticket.recordId,
        changes,
        initialValues,
        ticket.dateModified,
        force
      );
      // Success — clear local pending state and bubble updated ticket up
      setSoftWarning(result.softWarning || null);
      setConflict(null);
      if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
      // Reset form to the new server values
      // (initialValues will recompute when parent updates `tickets` and re-renders this component with new ticket prop)
    } catch (err) {
      if (err.hardConflict) {
        setConflict({
          lastModifiedBy: err.lastModifiedBy,
          lastModifiedAt: err.lastModifiedAt,
          conflictedFields: err.conflictedFields,
        });
      } else if (err.dispatched) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage(err.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const doDispatch = async () => {
    setDispatching(true);
    setErrorMessage(null);
    try {
      const result = await dispatchTicketAction(ticket.recordId, 'dispatch');
      if (onTicketUpdated && result.ticket) onTicketUpdated(result.ticket);
      setDispatchConfirmOpen(false);
    } catch (err) {
      setErrorMessage(err.message || 'Dispatch failed');
    } finally {
      setDispatching(false);
    }
  };

  const fieldClass = (key) => `edit-field ${changes[key] !== undefined ? 'edit-field-changed' : ''}`;

  return (
    <div className="detail-content editable-detail">
      <h4>
        <a href={qbUrl} target="_blank" rel="noopener noreferrer" title="Open ticket in Quickbase">
          {ticket.siteName || 'Unknown Site'}
        </a>
      </h4>
      <CheckInCard checkIn={ticket.checkIn} />
      <div className="detail-field">
        <span>Record ID:</span> {ticket.recordId} <a href={qbUrl} target="_blank" rel="noopener noreferrer" className="inline-link">QB↗</a>
        {ticket.fnWoId && <span style={{ marginLeft: 8, color: '#6b7280' }}>· FN WO# {ticket.fnWoId}</span>}
      </div>
      <div className="detail-field"><span>Project:</span> {ticket.projectName || '—'}</div>
      <div className="detail-field"><span>Service / Item:</span> {ticket.serviceType || '—'} / {ticket.itemName || '—'}</div>
      {ticket.appTemplateName && (
        <div className="detail-field"><span>App Template:</span> {ticket.appTemplateName}</div>
      )}
      <div className="detail-field"><span>Status:</span> {ticket.status || '—'}</div>
      <div className="detail-field"><span>Location:</span> {ticket.city}{ticket.state ? `, ${ticket.state}` : ''}</div>
      <div className="detail-field detail-description">
        <span>Description:</span>
        <p>{ticket.description || '—'}</p>
      </div>

      <SoftWarningBanner warning={softWarning} onDismiss={() => setSoftWarning(null)} />

      <CollapsibleSection title="Instructions" defaultOpen={false}>
        <div className={fieldClass('description')}>
          <label>Description</label>
          <div className="edit-input-wrap edit-input-wrap-textarea">
            <AutoTextarea
              value={values.description}
              onChange={e => setField('description', e.target.value)}
            />
          </div>
        </div>
        {ticket.toolsRequired && (
          <div className="detail-field detail-description">
            <span>Required Tools:</span>
            <p>{ticket.toolsRequired}</p>
          </div>
        )}
        {ticket.technicalScope && (
          <div className="detail-field detail-description">
            <span>Technical Scope:</span>
            <p>{ticket.technicalScope}</p>
          </div>
        )}
        <div className={fieldClass('siteVisitInstructions')}>
          <label>Site Visit Instructions</label>
          <div className="edit-input-wrap edit-input-wrap-textarea">
            <AutoTextarea
              value={values.siteVisitInstructions}
              onChange={e => setField('siteVisitInstructions', e.target.value)}
            />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Payment" defaultOpen={false}>
        <div className={fieldClass('payType')}>
          <label>Pay Type</label>
          <div className="edit-input-wrap">
            <select value={values.payType} onChange={e => setField('payType', e.target.value)}>
              <option value="">— None —</option>
              {PAY_TYPE_CHOICES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ClearBtn show={!!values.payType} onClick={() => setField('payType', '')} />
          </div>
        </div>
        {[
          ['fixedRate', 'Fixed Rate'],
          ['firstHourRate', 'First Hour Rate'],
          ['hourlyRate', 'Hourly Rate'],
          ['travel', 'Travel'],
          ['minHours', 'Min Hours'],
          ['expectedHours', 'Expected Hours'],
          ['maxHours', 'Max Hours'],
        ].map(([key, label]) => (
          <div key={key} className={fieldClass(key)}>
            <label>{label}</label>
            <div className="edit-input-wrap">
              <input
                type="number"
                step="any"
                value={values[key] ?? ''}
                onChange={e => setField(key, e.target.value)}
              />
              <ClearBtn show={values[key] !== '' && values[key] !== null && values[key] !== undefined} onClick={() => setField(key, '')} />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {!ticket.fieldRepName && (
        <CollapsibleSection title="Send Opportunity" defaultOpen={false}>
          <OpportunityPanel ticket={ticket} fieldReps={fieldReps} onTicketUpdated={onTicketUpdated} />
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Scheduling">
        <div className={fieldClass('scheduleDate')}>
          <label>Schedule Date</label>
          <div className="edit-input-wrap">
            <input type="date" value={values.scheduleDate} onChange={e => setField('scheduleDate', e.target.value)} />
            <ClearBtn show={!!values.scheduleDate} onClick={() => setField('scheduleDate', '')} />
          </div>
        </div>
        <div className="detail-field"><span>Start Date:</span> {formatDatePST(ticket.startDate)}</div>
        <div className="detail-field"><span>Due Date:</span> {formatDatePST(ticket.dueDate)}</div>
      </CollapsibleSection>

      <TicketMessagesSection
        ticket={ticket}
        canEdit
        openMessageCounts={openMessageCounts}
        onThreadClosed={onThreadClosed}
        onThreadCreated={onThreadCreated}
        fieldReps={fieldReps}
      />

      <CollapsibleSection title="Field Rep">
        <div className={fieldClass('relatedFieldRepId')}>
          <RepSearchSelect
            value={values.relatedFieldRepId}
            onChange={(repId) => setField('relatedFieldRepId', repId)}
            fieldReps={fieldReps}
            nearbyJson={ticket.nearbyFieldRepsJson}
          />
        </div>
        {(() => {
          const selectedRep = findFieldRep(fieldReps, values.relatedFieldRepId);
          return selectedRep ? <RepDetail rep={selectedRep} hideName /> : null;
        })()}
      </CollapsibleSection>

      <CollapsibleSection title="Field Nation" defaultOpen={false}>
        <FieldNationSection ticket={ticket} canEdit onTicketUpdated={onTicketUpdated} />
      </CollapsibleSection>

      <CollapsibleSection title="Confirmations" defaultOpen={false}>
        <ConfirmationsSection ticket={ticket} canEdit onTicketUpdated={onTicketUpdated} />
      </CollapsibleSection>

      {partsRequired && (
        <CollapsibleSection
          title="Shipping"
          defaultOpen={false}
          open={shippingOpen}
          onOpenChange={setShippingOpen}
        >
          <div className={`${fieldClass('shipToLocation')}${shipToMissing ? ' edit-field-required' : ''}`}>
            <label>Ship To{shipToRequired && <span className="edit-required-mark">*</span>}</label>
            <div className="edit-input-wrap">
              <select value={values.shipToLocation} onChange={e => setField('shipToLocation', e.target.value)}>
                <option value="">— None —</option>
                {SHIP_TO_CHOICES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <ClearBtn show={!!values.shipToLocation} onClick={() => setField('shipToLocation', '')} />
            </div>
            {shipToMissing && (
              <div className="edit-field-hint">Required when assigning a rep to a parts-required ticket.</div>
            )}
          </div>
          <PartsTrackingDisplay ticket={ticket} />
        </CollapsibleSection>
      )}

      {errorMessage && <div className="edit-error">{errorMessage}</div>}

      <div className="edit-footer">
        <button
          className="edit-cancel"
          onClick={() => { setValues(initialValues); setSoftWarning(null); setErrorMessage(null); }}
          disabled={saving || pendingCount === 0}
        >
          Cancel
        </button>
        <button
          className="edit-save"
          onClick={() => doSave(false)}
          disabled={saving || pendingCount === 0 || shipToMissing}
          title={shipToMissing ? 'Select a Ship To location before saving' : ''}
        >
          {saving ? 'Saving…' : `Save to Ticket (${pendingCount})`}
        </button>
      </div>

      {ticket.relatedFieldRepId && (
        <div className="action-buttons">
          <button
            className="action-btn action-btn-primary"
            onClick={() => setDispatchConfirmOpen(true)}
            disabled={dispatching || pendingCount > 0}
            title={pendingCount > 0 ? 'Save changes before dispatching' : ''}
          >
            {dispatching ? 'Dispatching…' : 'Dispatch'}
          </button>
        </div>
      )}

      <ConflictPanel
        conflict={conflict}
        onCancel={() => setConflict(null)}
        onSubmitAnyway={() => doSave(true)}
        submitting={saving}
      />

      <a href={qbUrl} target="_blank" rel="noopener noreferrer" className="detail-link">
        View in QuickBase
      </a>

      <ActionConfirmModal
        open={dispatchConfirmOpen}
        title="Dispatch this ticket?"
        message={ticket.fieldRepName
          ? `Dispatch this ticket to ${ticket.fieldRepName}?`
          : 'Dispatch this ticket to the assigned rep?'}
        confirmLabel="Dispatch"
        busy={dispatching}
        onCancel={() => setDispatchConfirmOpen(false)}
        onConfirm={doDispatch}
      />
    </div>
  );
}

function RepDetail({ rep, hideName = false }) {
  const qbUrl = `https://siteservicesnow.quickbase.com/nav/app/bjt99j3pb/table/bjt99p7ie/action/dr?rid=${rep.repId}`;

  const last12 = rep.last12Months;
  const isDirectDeposit = (rep.paymentMethod || '').toLowerCase().includes('direct deposit');

  const rates = [
    ['Pay Type', rep.defaultPayType],
    ['First Hour', formatCurrency(rep.defaultFirstHour)],
    ['Hourly Rate', formatCurrency(rep.defaultHourlyRate)],
    ['Min Hours', rep.defaultMinHours],
    ['Min Fee/Visit', formatCurrency(rep.defaultMinFeePerVisit)],
    ['Fixed/Daily', formatCurrency(rep.defaultFixedRate)],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div className="detail-content">
      {!hideName && <h4>{rep.name || 'Unknown Rep'}</h4>}

      {/* Contact / payment / address (was the "Identity" block; header
          dropped, Rep ID dropped — both already on the marker title). */}
      <div className="detail-field"><span>Phone:</span> {rep.phone || '—'}</div>
      <div className="detail-field"><span>Email:</span> {rep.email || '—'}</div>
      <div className="detail-field"><span>Payment:</span> {rep.paymentMethod || '—'}</div>
      {isDirectDeposit && (
        <div className="detail-field">
          <span>COI Compliant:</span>{' '}
          {rep.coiCompliant === true
            ? <span style={{ color: '#15803d' }}>✓ Yes</span>
            : <span style={{ color: '#b91c1c' }}>⚠ No</span>}
        </div>
      )}
      {(rep.street1 || rep.city || rep.state) && (
        <div className="detail-field detail-description">
          <span>Address:</span>
          <p>
            {rep.street1 && <>{rep.street1}<br /></>}
            {rep.street2 && <>{rep.street2}<br /></>}
            {[rep.city, rep.state, rep.postalCode].filter(Boolean).join(' ')}
          </p>
        </div>
      )}

      <div className="detail-field"><span>Rating:</span> {renderStars(rep.rating)}</div>
      {rep.otrResAbdnHtml && (
        <div className="detail-field">
          <span>OTR/RES/ABDN:</span>
          <ComplianceBar html={rep.otrResAbdnHtml} />
        </div>
      )}
      {last12 != null && last12 !== '' && (
        <div className="detail-field"><span>Last 12 Mo:</span> {last12} visits</div>
      )}
      {rates.map(([label, val]) => (
        <div key={label} className="detail-field"><span>{label}:</span> {val}</div>
      ))}

      <a href={qbUrl} target="_blank" rel="noopener noreferrer" className="detail-link">
        View Profile in QuickBase
      </a>
    </div>
  );
}
